/**
 * Vertical bar chart of average service execution duration grouped by
 * service type. Uses ACCEPTED -> FINISHED interval, so cancelled /
 * declined work doesn't skew the value.
 *
 * The BE only knows serviceTypeId; we look up a human label via the
 * `serviceTypes` lookup passed in from the page (sourced from Redux).
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

import ChartCard from "../ChartCard/ChartCard";
import {
  CHART_PALETTE,
  formatSec
} from "../../../../../constants/analytics.constants";

const TooltipBox = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div
      style={{
        background: CHART_PALETTE.ink,
        color: "#fff",
        fontSize: 11,
        padding: "6px 8px",
        borderRadius: 6,
        boxShadow: "0px 2px 4px rgba(0,0,0,0.25)"
      }}
    >
      <div style={{ color: "#A9A7BD", fontSize: 10 }}>{p.label}</div>
      <div style={{ fontWeight: 600, fontSize: 12, marginTop: 1 }}>
        {formatSec(p.avgSeconds)}
      </div>
    </div>
  );
};

const DurationByTypeChart = ({ data = [], serviceTypes = [] }) => {
  const labelById = serviceTypes.reduce((acc, t) => {
    acc[t.id] = t.name || t.key || `Type #${t.id}`;
    return acc;
  }, {});

  const labelled = data.map(d => ({
    ...d,
    label: labelById[d.serviceTypeId] || `Type #${d.serviceTypeId}`
  }));

  return (
    <ChartCard
      title="Avg duration by service type"
      subtitle="STARTED → FINISHED, finished services only"
    >
      {labelled.length === 0 ? (
        <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#8E8E8E", fontSize: 12 }}>
          No finished services in this range
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={labelled}>
            <CartesianGrid strokeDasharray="3 4" stroke={CHART_PALETTE.inkLine} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: CHART_PALETTE.axis }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatSec}
              tick={{ fontSize: 10, fill: CHART_PALETTE.axis }}
              axisLine={false}
              tickLine={false}
              width={70}
            />
            <Tooltip content={<TooltipBox />} cursor={{ fill: "rgba(41,35,89,0.04)" }} />
            <Bar dataKey="avgSeconds" radius={[3, 3, 0, 0]}>
              {labelled.map((_, i) => (
                <Cell key={i} fill={CHART_PALETTE.inkSoft} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
};

export default DurationByTypeChart;
